// @ts-check
'use strict';
//***********************************************************************************
//***********************************************************************************
//******     CN-Map    **************************************************************
//******     Copyright(C) 2019-2020 EnerBIM                        ******************
//***********************************************************************************
//***********************************************************************************

//***********************************************************************************
//***********************************************************************************
//**** Balcony type
//***********************************************************************************
//***********************************************************************************

import { cn_element_type } from './cn_element_type';
import { cn_configuration, cn_configuration_choice, cn_configuration_line, cn_configuration_param, cn_configuration_param_group, cn_configuration_tab } from './cn_configuration';
import { fh_add, fh_clone, fh_cross, fh_extruded_polygon, fh_mul, fh_normalize, fh_sub } from '@acenv/fh-3d-viewer';
import { cn_element_type_layer_material } from './cn_element_type_layer_material';
import { cn_material } from './cn_material';
import { code_to_label } from './cn_opening_type';
import { cn_middle, cn_translate_polygon, cnx_clone } from '../utils/cn_utilities';
import { cn_element_type_visitor } from '../utils/visitors/cn_element_type_visitor';

var CN_BT_MIDDLE = 0;
var CN_BT_OUTER = 1;
var CN_BT_INNER = 2;

var CN_BT_CATEGORY_LIST = [
    { label: 'Fer forgé', code: 'railing', filled: false, color: [50, 100, 50] },
    { label: 'Bois', code: 'wood', filled: false, color: [150, 125, 100] },
    { label: 'Cable', code: 'cable', filled: false, color: [100, 100, 100] },
    { label: 'Tole', code: 'plate', filled: true, color: [50, 50, 50], filling: [50, 50, 50] },
    { label: 'Verre', code: 'glass', filled: true, color: [50, 50, 100], filling: [200, 200, 255, 100] },
    { label: 'Béton', code: 'wall', filled: true, color: [150, 150, 150] },
    { label: 'Jardinière', code: 'planter', filled: true, color: [150, 150, 150] }
];

export class cn_balcony_type extends cn_element_type_layer_material {
    constructor(parent) {
        super(parent);
        this.class_name = 'cn_balcony_type';
        this.name = '';
        this.height = 1.1;
        this.planter_width = 0;
        this.category = 'railing';

        this.free = false;
        this.layers = [cn_material.material_by_code(this.parent, 'unknown', 0.1)];
    }

    //***********************************************************************************
    get_generic_label() {
        return 'Type de balcon';
    }

    //***********************************************************************************
    /**
     * Clone
     * @returns {cn_element_type}
     */
    clone() {
        var c = new cn_balcony_type(this.parent);
        c.name = this.name;
        c.height = this.height;
        c.category = this.category;
        c.planter_width = this.planter_width;
        c.is_expert = this.is_expert;
        c.layers = [];
        for (var i in this.layers)
            c.layers.push(this.layers[i].clone());
        return c;
    }

    //***********************************************************************************
    //**** keys
    //***********************************************************************************
    model_keys() {
        return ['name', 'height', 'layers', 'category', 'planter_width', 'is_expert'];
    }

    //***********************************************************************************
    //**** set category
    //***********************************************************************************
    set_category(category, actual_change = false) {
        if (this.category == category) return;
        this.category = category;
        if (actual_change) {
            let thickness = (this.category == 'wall') ? 0.2 : (this.category == 'planter') ? 0.5 : 0.1;
            this.layers = [cn_material.material_by_code(this.parent, 'unknown', thickness)];
        }
    }

    //***********************************************************************************
    //**** default
    //***********************************************************************************
    /**
     *
     * @returns cn_element_type
     */
    static default_railing(parent) {
        return new cn_balcony_type(parent);
    }

    /**
     *
     * @returns cn_element_type
     */
    static default_wall(parent) {
        var bt = new cn_balcony_type(parent);
        bt.height = 1.1;
        this.layers = [cn_material.material_by_code(bt.parent, 'unknown', 0.2)];
        bt.category = 'wall';
        return bt;
    }

    /**
     * Returns 'true' if balcony is a railing or similar.
     * @returns {boolean}
     */
    is_railing() {
        return (this.category != 'wall' && this.category != 'planter');
    }

    //***********************************************************************************
    //**** serialize
    //***********************************************************************************
    serialize() {
        return this._serialize(false)
    }

    serialize_building_agnostic() {
        return this._serialize(true)
    }

    _serialize(building_agnostic) {
        var json = this.serialize_element();
        json.class_name = 'cn_balcony_type';
        json.name = this.name;
        json.height = this.height;
        json.planter_width = this.planter_width;
        json.category = this.category;
        json.is_expert = this.is_expert;
        json.layers = [];
        for (var i in this.layers)
            json.layers.push(this.layers[i]._serialize(building_agnostic));
        return json;
    }

    static unserialize(json, building) {
        var wt = new cn_balcony_type(building);
        wt.unserialize_element(json);
        wt.name = json.name;
        wt.height = json.height;
        if (typeof (json.category) == 'string' && code_to_label(json.category, CN_BT_CATEGORY_LIST))
            wt.category = json.category;
        if (typeof (json.planter_width) == 'number') wt.planter_width = json.planter_width;
        if (typeof (json.is_expert) == 'boolean') wt.is_expert = json.is_expert;

        wt.layers = [];
        if (typeof (json.layers) == 'object' && json.layers.length > 0) {
            for (var i in json.layers) {
                var layer = cn_material.unserialize(json.layers[i], building);
                if (layer == null) continue;
                wt.layers.push(layer);
            }
        }
        else if (typeof (json.thickness) == 'number')
            wt.layers = [cn_material.material_by_code(building, 'unknown', json.thickness)];

        if (wt.layers.length == 0)
            wt.layers = [cn_material.material_by_code(building, 'unknown', 0.2)];

        return wt;
    }

    /**
     * Switch to expert or non-expert.
     * @param {boolean} v
     */
    set_expert(v) {
        super.set_expert(v);
        // also must set category & free attributes
        this.category = 'wall';
        this.free = false;
    }

    //***********************************************************************************
    /**
     * label
     * @returns {string}
     */
    get_label() {
        if (this.name != '') return this.name;
        var element_type_name = this.get_category_label();
        element_type_name += ' ' + (this.height * 100).toFixed(0) + 'cm';
        return element_type_name;
    }

    //***********************************************************************************
    /**
     * Category label
     * @returns {string}
     */
    get_category_label() {
        if (this.is_expert) {
            return 'Balcon expert'
        }
        return code_to_label(this.category, CN_BT_CATEGORY_LIST);
    }

    //***********************************************************************************
    //**** Returns description
    //***********************************************************************************

    /**
     * Returns an array of objects describing the type.
     * @returns {{label: string, value?: any, decimals?: number, unit?: string}[]}
     */
    get_description() {
        var description = [];

        var cat = { label: 'Catégorie' };
        cat.value = this.get_category_label();
        description.push(cat);

        var height = { label: 'Hauteur' };
        description.push(height);
        height.value = this.height * 100;
        height.decimals = 0;
        height.unit = 'cm';

        var th = { label: 'Epaisseur' };
        description.push(th);
        th.value = this.thickness * 100;
        th.decimals = 0;
        th.unit = 'cm';

        if (this.is_expert) {
            var layers = { label: 'Matériaux' };
            description.push(layers);
            layers.value = [];

            for (var i in this.layers) {
                layers.value.push(this.layers[i].get_label());
            }
        }

        return description;
    }

    //***********************************************************************************
    /**
     * draw svg icon
     * @param {number} width
     * @param {number} height
     * @returns {string}
     */
    draw_svg_icon(width, height) {
        var html = '';
        var z = height * 0.2;
        const cat = CN_BT_CATEGORY_LIST.find(ct => ct.code == this.category);
        var color = `rgb(${cat.color[0]},${cat.color[1]},${cat.color[2]})`;
        if (this.category == 'wall') {
            var sz = [width, height];
            var p0 = [0, 0];
            var p1 = [width, height];
            var scale = width;
            let x0 = sz[0] * (0.5 - this.thickness * 0.5);
            p0[0] = x0;
            for (let i = 0; i < this.layers.length; i++) {
                p1[0] = p0[0];
                const vertical_line = '<line x1=\'' + p0[0] + '\' y1=\'' + p0[1] + '\' x2=\'' + p1[0] + '\' y2=\'' + p1[1] + '\' style=\'stroke:black\' />';
                p1[0] += scale * this.layers[i].thickness;
                html += this.layers[i].draw_material(p0, p1[0] - p0[0], p1[1] - p0[1], scale);
                html += vertical_line;
                p0[0] = p1[0];
                if (i == this.layers.length - 1)
                    html += '<line x1=\'' + p0[0] + '\' y1=\'' + p0[1] + '\' x2=\'' + p1[0] + '\' y2=\'' + p1[1] + '\' style=\'stroke:black\' />';
            }
            html += `<rect x="${x0}" y="${p0[1]}" width="${scale * this.thickness} " height="${p1[1] - p0[1]} " style="fill: none; stroke: black; " />`;
        } else if (this.category == 'planter') {
            z = height * 0.4;
            html += '<rect x=\'0\' y=\'' + z + '\' width=\'' + width + '\' height=\'' + (height - z) + '\' style=\'fill:rgb(150,150,150);stroke:black;\' />';
        } else {
            var ramp_width = height * 0.05;
            var rail_size = height * 0.02;
            var rail_height = height * 0.1;
            html += '<rect x=\'0\' y=\'' + z + '\' width=\'' + width + '\' height=\'' + ramp_width + '\' style=\'fill:' + color + ';stroke:black;\' />';
            if (!cat.filling)
                html += '<rect x=\'0\' y=\'' + (height - rail_height) + '\' width=\'' + width + '\' height=\'' + rail_size + '\' style=\'fill:' + color + ';stroke:black;\' />';
            var h = height - z - ramp_width;
            for (var n = 0; n < 2; n++) {
                var x = (n == 0) ? 0 : width - rail_size;
                html += '<rect x=\'' + x + '\' y=\'' + (z + ramp_width) + '\' width=\'' + rail_size + '\' height=\'' + h + '\' style=\'fill:' + color + ';stroke:black;\' />';
            }

            if (this.category == 'railing' || this.category == 'wood') {
                var nb_rails = 8;
                h = height - z - ramp_width - rail_height;
                var rail_spacing = (width - rail_size) / (nb_rails - 1);
                var x = rail_spacing;
                for (var n = 1; n < nb_rails - 1; n++) {
                    html += '<rect x=\'' + x + '\' y=\'' + (z + ramp_width) + '\' width=\'' + rail_size + '\' height=\'' + h + '\' style=\'fill:' + color + ';stroke:black;\' />';
                    x += rail_spacing;
                }
            } else if (cat.filling) {
                var filling_color = `rgb(${cat.filling[0]},${cat.filling[1]},${cat.filling[2]})`;
                h = height - z - 3 * ramp_width - rail_height;
                html += '<rect x=\'' + rail_size + '\' y=\'' + (z + 3 * ramp_width) + '\' width=\'' + (width - 2 * rail_size) + '\' height=\'' + h + '\' style=\'fill:' + filling_color + ';stroke:black;\' />';
            } else if (this.category == 'cable') {
                var nb_cables = 5;
                h = height - z - 3 * ramp_width - rail_height;
                for (var n = 0; n < nb_cables; n++) {
                    var y = z + ramp_width + (n + 1) * h / nb_cables;
                    html += `<line x1="${rail_size}" y1="${y}" x2="${width - rail_size}" y2="${y}" style="fill:none;stroke:black;" />`;
                }
            }
        }
        return html;
    }

    //***********************************************************************************
    //**** 3D geometry
    //***********************************************************************************
    build_extruded_polygons(z, wall_interface) {
        var extruded_polygons = [];

        //*** Wall balcony
        const cat = CN_BT_CATEGORY_LIST.find(ct => ct.code == this.category);
        const color = cat.color.map(c => c / 255);
        if (this.category == 'wall' || this.category == 'planter') {
            if (this.height <= 0 || this.thickness <= 0) return extruded_polygons;
            var epg = new fh_extruded_polygon();
            extruded_polygons.push(epg);
            epg.polygon = wall_interface.footprint.clone();
            cn_translate_polygon(epg.polygon, [0, 0, z]);
            epg.direction = [0, 0, this.height];
            epg.color = color;

            if (this.category == 'planter') {
                const hole = epg.polygon.clone();
                hole.offset(-0.05, 2);
                epg.polygon.substracts(hole);

                var epg = new fh_extruded_polygon();
                extruded_polygons.push(epg);
                epg.polygon = hole;
                epg.direction = [0, 0, this.height - 0.05];
                epg.color = [80, 138, 44].map(x => x / 255);
            }
        }
        //*** railing balcony
        else {
            var ramp_thickness = 0.03;
            var bottom_height = 0.1;
            var rail_thickness = 0.015;
            var start_rail_thickness = 0.03;

            var v0 = cnx_clone(cn_middle(wall_interface.shape[0], wall_interface.shape[1]), z);
            var v1 = cnx_clone(cn_middle(wall_interface.shape[2], wall_interface.shape[3]), z);
            var direction = fh_sub(v1, v0);
            var length = fh_normalize(direction);
            var dz = [0, 0, 1];
            var dx = fh_cross(direction, dz);

            var p0 = fh_clone(v0);

            //*** ramp
            p0[2] = z + this.height - ramp_thickness * 0.5;
            var epg = new fh_extruded_polygon();
            extruded_polygons.push(epg);
            epg.tube(p0, fh_mul(direction, length), dx, this.thickness, ramp_thickness);
            epg.color = color;

            //*** Bottom bar
            if (!cat.filling) {
                p0[2] = z + bottom_height;
                p0 = fh_add(v0, fh_mul(dz, bottom_height));
                epg = new fh_extruded_polygon();
                extruded_polygons.push(epg);
                epg.tube(p0, fh_mul(direction, length), dx, rail_thickness, rail_thickness);
                epg.color = color;
            }

            //*** start rails
            epg = new fh_extruded_polygon();
            extruded_polygons.push(epg);
            epg.tube(v0, fh_mul(dz, this.height), dx, start_rail_thickness, start_rail_thickness);
            epg.color = color;

            epg = new fh_extruded_polygon();
            extruded_polygons.push(epg);
            epg.tube(v1, fh_mul(dz, this.height), dx, start_rail_thickness, start_rail_thickness);
            epg.color = color;

            //*** rails
            if (this.category == 'railing' || this.category == 'wood') {
                var nb_rails = 1 + Math.floor(length / (0.11 - rail_thickness));
                var rail_spacing = length / nb_rails;
                var ez = fh_mul(dz, this.height - bottom_height - ramp_thickness - rail_thickness * 0.5);
                p0 = fh_add(v0, fh_mul(dz, bottom_height + rail_thickness * 0.5));
                for (var n = 1; n < nb_rails; n++) {
                    epg = new fh_extruded_polygon();
                    extruded_polygons.push(epg);
                    epg.tube(fh_add(p0, fh_mul(direction, rail_spacing * n)), ez, dx, rail_thickness, rail_thickness);
                    epg.color = color;
                }
            }

            //*** cables */
            if (this.category == 'cable') {
                var nb_cables = 1 + Math.floor((this.height - bottom_height) / 0.11);
                var cable_spacing = (this.height - bottom_height) / nb_cables;
                for (var n = 1; n < nb_cables; n++) {
                    epg = new fh_extruded_polygon();
                    extruded_polygons.push(epg);
                    p0[2] = z + bottom_height + n * cable_spacing;
                    epg.tube(p0, fh_mul(direction, length), dx, 0.005, 0.005);
                    epg.color = color;
                }
            }

            if (cat.filling) {
                epg = new fh_extruded_polygon();
                extruded_polygons.push(epg);
                p0[2] = z + bottom_height;
                epg.brick(p0, fh_mul(direction, length), fh_mul(dx, 0.01), fh_mul(dz, this.height - 2 * bottom_height));
                epg.color = cat.filling.map(c => c / 255);
            }
        }
        return extruded_polygons;
    }

    //***********************************************************************************
    //**** Get configuration
    //***********************************************************************************
    static configuration() {
        var configuration = new cn_configuration();

        var param_group = new cn_configuration_param_group('Dimensions', 'dimensions');
        configuration.add_global_param_group(param_group);
        param_group.add_param(new cn_configuration_param('Hauteur', 'height', 30, 200, 'cm'));

        //***********************************************
        //*** Railing configuration
        var railing_unfilled = new cn_configuration_tab('Garde-corps ajouré', 'unfilled');
        configuration.add_tab(railing_unfilled);

        var railing_unfilled_list = new cn_configuration_line('Balustrade');
        railing_unfilled.add_line(railing_unfilled_list);

        railing_unfilled_list.add_param(new cn_configuration_param('Epaisseur', 'thickness', 1, 100, 'cm'));

        CN_BT_CATEGORY_LIST.filter(category => !category.filled).forEach(category => {
            railing_unfilled_list.add_choice(new cn_configuration_choice(category.label, category.code));
        });

        //***********************************************
        //*** Railing configuration
        var railing_filed = new cn_configuration_tab('Garde-corps plein', 'filled');
        configuration.add_tab(railing_filed);

        var railing_filed_list = new cn_configuration_line('Balustrade');
        railing_filed.add_line(railing_filed_list);

        railing_filed_list.add_param(new cn_configuration_param('Epaisseur', 'thickness', 1, 100, 'cm'));

        CN_BT_CATEGORY_LIST.filter(category => category.filled).forEach(category => {
            railing_filed_list.add_choice(new cn_configuration_choice(category.label, category.code));
        });

        return configuration
    }

    //***********************************************************************************
    //**** Fill configuration
    //***********************************************************************************
    fill_configuration(config = null) {
        var configuration = (config) ? config : cn_balcony_type.configuration();
        configuration.get_global_param_group('dimensions').set_param('height', this.height * 100);

        const cat = CN_BT_CATEGORY_LIST.find(ct => ct.code == this.category);

        //*** default to railing
        configuration.selection = (cat.filled) ? 1 : 0;

        const line = configuration.tabs[configuration.selection].lines[0];
        line.set_selection(this.category);

        configuration.tabs[0].lines[0].get_param('thickness').value = this.thickness * 100;
        configuration.tabs[1].lines[0].get_param('thickness').value = this.thickness * 100;

        return configuration;
    }

    //***********************************************************************************
    //**** Load configuration
    //***********************************************************************************
    load_configuration(configuration, actual_change = false) {
        const configuration_line = configuration.tabs[configuration.selection].lines[0];
        const dont_read_thickness = (actual_change && this.category != configuration_line.get_selection())
        this.set_category(configuration_line.get_selection(), actual_change);
        if (!dont_read_thickness && !this.is_expert) {
            const thickness = 0.01 * configuration_line.get_param('thickness').value;
            this.layers = [cn_material.material_by_code(this.parent, 'unknown', thickness)];
        }
        this.height = 0.01 * configuration.get_global_param_group('dimensions').get_param('height').value;
        return true;
    }

    /**
     * Accept element type visitor
     *
     * @param {cn_element_type_visitor} element_type_visitor
     */
    accept_visitor(element_type_visitor) {
        element_type_visitor.visit_balcony_type(this);
    }

    /**
     * returns true if wall geometry hasn't changed since date
     * @param {number} date
     * @returns {boolean}
     */
    up_to_date_thickness(date) {
        if (this.get_date() <= date) return true;
        if (this.get_date('thickness') > date) return false;
        return true;
    }
}

